import React, { useCallback, useMemo, useState } from "react";
import Flex from "../../structure/Flex";
import Icon from "../Icon";
import ImageSlideshow from "../ImageSlideshow";

interface ImageGalleryProps {
  images: any;
  close: () => void;
  localMap?: boolean;
}

interface GalleryImage {
  image: string;
  title?: string;
}

interface ProcessedImage {
  image: string;
  name?: string;
  title?: string;
  index?: number;
  id: string;
}

interface ImageCardProps {
  data: ProcessedImage;
  openGallery: (index: number) => void;
}

const isVideoFile = (url?: string): boolean => {
  if (!url) return false;
  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".avi"];
  return videoExtensions.some((ext) => url.toLowerCase().endsWith(ext));
};

export const VideoModal: React.FC<{
  videoSrc: string;
  onClose: () => void;
}> = ({ videoSrc, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black"
    onClick={onClose}
  >
    <div
      className="relative py-6 w-full h-auto md:h-full md:w-full"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className="fixed right-0 mr-6 mt-10 top-0 z-50 text-white hover:text-gray-300 focus:outline-none md:absolute"
        onClick={onClose}
        aria-label="Close video"
      >
        <Icon className="text-3xl">close</Icon>
      </button>
      <video
        src={videoSrc ? `${videoSrc}?w=1080` : ""}
        autoPlay
        controls
        loop
        className="h-full w-full object-contain"
        playsInline
      />
    </div>
  </div>
);

const ImageCard: React.FC<ImageCardProps> = ({ data, openGallery }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const handleImageLoad = useCallback(() => setImageLoaded(true), []);

  if (!data) return null;

  const { image, name, index = 0 } = data;
  const isVideo = isVideoFile(image);

  return (
    <section
      className="relative flex-shrink-0 w-full h-full overflow-hidden rounded-lg shadow-xs cursor-pointer hover:shadow-md"
      onClick={() => openGallery(index)}
    >
      <Flex className="flex-col sm:flex-row">
        {isVideo ? (
          <video
            src={image ? `${image}?w=320` : ""}
            className="object-cover w-full h-32 transition duration-500 transform sm:h-80 hover:scale-110 bg-subtitle"
            autoPlay
            muted
            loop
          />
        ) : image ? (
          <>
            {!imageLoaded && (
              <div className="flex-shrink-0 w-full h-32 md:h-80 bg-white" />
            )}
            <img
              src={`${image}?h=320`}
              alt={name || "Gallery image"}
              onLoad={handleImageLoad}
              className="object-cover w-full h-32 transition duration-500 transform sm:h-80 hover:scale-110 bg-subtitle"
            />
          </>
        ) : (
          <div className="flex-shrink-0 w-full h-full bg-subtitle" />
        )}
      </Flex>
    </section>
  );
};

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  close,
  localMap,
}) => {
  const [galleryVisible, setGalleryVisible] = useState<boolean>(false);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<string | null>(
    images?.length > 0 ? images[0].title : null
  );
  const [videoSrc, setVideoSrc] = useState<string | null>(null);

  const filteredImages = useMemo(() => {
    if (localMap) {
      return images.map((item: any, index: number) => {
        return {
          image: item.image,
          index,
          id: `image-${index}`,
        };
      });
    }

    if (!selectedTitle) return [];

    const category = images.find((item: any) => item.title === selectedTitle);
    if (!category?.images) return [];

    return category.images.map((item: any, index: number) => {
      if (typeof item === "string") {
        return {
          image: item,
          index,
          id: `image-${index}`,
        };
      }
      return {
        image: item.image,
        name: item.title,
        title: item.title,
        index,
        id: `image-${index}`,
      };
    });
  }, [images, selectedTitle, localMap]);

  const openGallery = useCallback(
    (index: number) => {
      const selectedImage = filteredImages[index];
      if (selectedImage && isVideoFile(selectedImage.image)) {
        setVideoSrc(selectedImage.image);
      } else {
        setSelectedImageId(selectedImage?.id);
        setGalleryVisible(true);
      }
    },
    [filteredImages]
  );

  const handleCloseVideo = useCallback(() => setVideoSrc(null), []);
  const handleClose = useCallback(() => setGalleryVisible(false), []);
  const selectCategory = useCallback(
    (title: string) => setSelectedTitle(title),
    []
  );

  return (
    <>
      {videoSrc && (
        <VideoModal videoSrc={videoSrc} onClose={handleCloseVideo} />
      )}
      {galleryVisible ? (
        <ImageSlideshow
          close={handleClose}
          selectedImageId={selectedImageId}
          images={filteredImages}
        />
      ) : (
        <>
          <button
            className="fixed top-0 right-0 z-50 m-4 leading-none text-primary focus:outline-none"
            onClick={close}
            aria-label="Close gallery"
          >
            <Icon className="text-xl sm:text-3xl">close</Icon>
          </button>
          <h2 className="text-2xl font-medium text-left sm:text-4xl mb-6 md:mb-10">
            {localMap ? "Map" : "Gallery"}
          </h2>
          {!localMap && (
            <div className="mb-6 md:mb-10 overflow-x-auto scroll-hidden">
              <div className="whitespace-no-wrap">
                {images?.map((category: any) => (
                  <button
                    key={category.title}
                    className={`${
                      category.title === selectedTitle
                        ? "border border-orange"
                        : "border border-gray-200"
                    } text-primary px-4 py-2 m-1 rounded-lg focus:outline-none`}
                    onClick={() => selectCategory(category.title)}
                  >
                    {category.title}
                  </button>
                ))}
              </div>
            </div>
          )}
          <Flex className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {filteredImages.map((image: any, index: number) => (
              <ImageCard
                key={`image-${index}`}
                data={{ ...image, index }}
                openGallery={openGallery}
              />
            ))}
          </Flex>
        </>
      )}
    </>
  );
};

export default ImageGallery;
